import {Route, Routes} from "react-router-dom";
import {lazy, Suspense} from "react";
import {CircularLoading} from "@/components/Loading/CircularLoading/CircularLoading";
import UrlsConfig from "@/config/UrlsConfig";
import {useTranslation} from "react-i18next";
import {protectedRoutes} from "@/routes/protectedRoutes";
import {printOrderRoutes} from "@/routes/ProtectedRoutes/printOrderRoutes";
import {testingDesignRoutes} from "@/routes/TestingDesignRoutes";

const PedigreeEditorPrototype = lazy(() => import('@/pages/Test/PedigreeEditorPrototype'));

const Layout = lazy(() => import("@/components/Layout/Layout"));
const PartnerAddressOnboardingPage = lazy(() => import("@/pages/PartnerOnboarding/PartnerAddressOnboardingPage"));
const OnboardingLayout = lazy(() => import("@/features/PartnerOnboarding/components/OnboardingLayout"));
const OnboardingEntryChecker = lazy(() => import("@/routes/Checkers/OnboardingEntryChecker"));

const LoggedInChecker = lazy(() => import("@/pages/Auth/Login/Checkers/LoggedInChecker/LoggedInChecker"));
const Login = lazy(() => import("@/pages/Auth/Login/Login"));
const ForgotPassword = lazy(() => import("@/pages/Auth/ForgotPassword/ForgotPassword"));

const LoginAsUser = lazy(() => import("@/pages/Auth/LoginAsUser/LoginAsUser"));

const ForbiddenPage = lazy(() => import("@/pages/Error/ForbiddenPage"));
const NotAuthenticatedChecker = lazy(() => import("@/routes/Checkers/NotAuthenticatedChecker/NotAuthenticatedChecker"));
const TestOrderForm = lazy(() => import("@/pages/TestOrderForm/TestOrderForm"));
const TestOrdersTable = lazy(() => import("@/pages/TestOrderForm/TestOrdersTable"));
const TestAppealsTable = lazy(() => import("@/pages/TestOrderForm/TestAppealsTable"));
const TestOrderFormCanvas2 = lazy(() => import("@/pages/TestOrderForm/TestOrderFormCanvas2"));
const TestOpenedOrder = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrder"));
const TestAppealsPage2 = lazy(() => import("@/pages/TestOrderForm/TestAppealsPage2"));
const TestLayout = lazy(() => import("@/pages/TestOrderForm/TestLayout"));
const TestDesignSettings = lazy(() => import("@/pages/TestOrderForm/TestDesignSettings"));
const TestOrderFormV2 = lazy(() => import("@/pages/TestOrderForm/TestOrderFormV2"));
const TestOrderFormV3 = lazy(() => import("@/pages/TestOrderForm/TestOrderFormV3"));
const TestOpenedOrderV2 = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrderV2"));
const TestOpenedOrderTabs = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrderTabs"));
const TestOpenedOrderCommand = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrderCommand"));
const TestOpenedOrderCommandTabs = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrderCommandTabs"));
const TestOrderCommandPageCopy = lazy(() => import("@/pages/TestOrderForm/TestOrderCommandPageCopy"));
const TestOpenedOrderProcess = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrderProcess"));
const TestOpenedOrderSections = lazy(() => import("@/pages/TestOrderForm/TestOpenedOrderSections"));
const TestOrdersTableV2 = lazy(() => import("@/pages/TestOrderForm/TestOrdersTableV2"));
const PedigreeEditorModelPlayground = lazy(() => import("@/pages/Test/PedigreeEditorModelPlayground"));
const TestAddressWizardV4 = lazy(() => import("@/pages/TestPartnerOnboarding/TestAddressWizardV4"));
const TestPartnerProfile = lazy(() => import("@/pages/TestPartnerProfile/TestPartnerProfile"));
const TestPartnerProfileV2 = lazy(() => import("@/pages/TestPartnerProfile/TestPartnerProfileV2"));
const CleanDesignKitPage = lazy(() => import("@/pages/TestOrderForm/CleanDesignKitPage"));

const urlsConfig = new UrlsConfig();

export function AppRoutes() {
    const {t} = useTranslation();

    return (
        <Suspense fallback={<CircularLoading text={t("Loading...")}/> }>
            <Routes>
                <Route path={"/pedigree-editor-prototype"} element={<PedigreeEditorPrototype/>}/>

                <Route path="/partner-onboarding" element={<OnboardingEntryChecker/>}>
                    <Route element={<OnboardingLayout/>}>
                        <Route index element={<PartnerAddressOnboardingPage/>}/>
                    </Route>
                </Route>

                <Route element={<Layout/>}>

                  {/* LOGIN */}
                  <Route path={urlsConfig.login.to} element={<LoggedInChecker/>}>
                      <Route index element={<Login/>}/>
                  </Route>

                  <Route path={urlsConfig.forgotPassword.to} element={<LoggedInChecker/>}>
                      <Route path={urlsConfig.forgotPassword.to} element={<ForgotPassword/>}/>
                  </Route>

                    {/* LOGIN AS */}
                    <Route path={`${urlsConfig.loginAsUser.to}/:hash`} element={<LoginAsUser/>}/>

                    {/* PROTECTED ROUTES */}
                    {protectedRoutes()}

                    {/* FORBIDDEN PAGE */}
                    <Route path={urlsConfig.pageForbidden.to} element={<ForbiddenPage/>}/>
                </Route>

                {/* TEST PAGES — open, no auth, with TestLayout */}
                <Route element={<TestLayout/>}>
                    <Route path="/test-form" element={<TestOrderForm/>}/>
                    <Route path="/test-form-v2" element={<TestOrderFormV2/>}/>
                    <Route path="/test-form-v3" element={<TestOrderFormV3/>}/>
                    <Route path="/test-orders-table" element={<TestOrdersTable/>}/>
                    <Route path="/test-orders-table-v2" element={<TestOrdersTableV2/>}/>
                    <Route path="/test-appeals-table" element={<TestAppealsTable/>}/>
                    <Route path="/test-form-canvas" element={<TestOrderFormCanvas2/>}/>
                    <Route path="/test-order/:orderId" element={<TestOpenedOrder/>}/>
                    <Route path="/test-order-v2/:orderId" element={<TestOpenedOrderV2/>}/>
                    <Route path="/test-order-design-tabs/:orderId" element={<TestOpenedOrderTabs/>}/>
                    <Route path="/test-order-design-command/:orderId" element={<TestOpenedOrderCommand/>}/>
                    <Route path="/test-order-design-command-tabs/:orderId" element={<TestOpenedOrderCommandTabs/>}/>
                    <Route path="/test-order-design-command-copy/:orderId" element={<TestOrderCommandPageCopy/>}/>
                    <Route path="/test-order-design-process/:orderId" element={<TestOpenedOrderProcess/>}/>
                    <Route path="/test-order-design-sections/:orderId" element={<TestOpenedOrderSections/>}/>
                    <Route path="/test-appeals-page2" element={<TestAppealsPage2/>}/>
                    <Route path="/test-design-settings" element={<TestDesignSettings/>}/>
                    <Route path="/pedigree-editor-model-playground" element={<PedigreeEditorModelPlayground/>}/>

                    <Route path="/test-address-wizard" element={<TestAddressWizardV4/>}/>
                    <Route path="/test-partner-profile" element={<TestPartnerProfile/>}/>
                    <Route path="/test-partner-profile-v2" element={<TestPartnerProfileV2/>}/>

                    <Route path="/test-ui-kit" element={<CleanDesignKitPage/>}/>
                </Route>

                {testingDesignRoutes()}

                {/* PRINT PAGES — without Layout (no menu/header/footer) */}
                <Route element={<NotAuthenticatedChecker/>}>
                    {printOrderRoutes()}
                </Route>
            </Routes>
        </Suspense>
    );
}
